import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@clearcover/atlas';

import {
  buildPaymentPlanFrequencyOptions,
  mapPaymentPlanToOption,
} from 'QuoteApp/screens/Purchase/PriceCustomizer/utils';
import { getSelectedPaymentPlan } from 'QuoteApp/utils/paymentPlan';

import { AgentPaymentPreferenceFrequencySelector } from './AgentPaymentPreferenceFrequencySelector';
import {
  AgentPaymentPreferenceModalProps,
  FrequencyBannerMessages,
} from './types';
import lightbulbIcon from './assets/lightbulb.svg';
import styles from './styles.module.css';

const DEFAULT_FREQUENCY_BANNER_MESSAGES: FrequencyBannerMessages = {
  weekly: 'Weekly plans offer smaller, more frequent payments.',
};

export const AgentPaymentPreferenceModal: FC<
  AgentPaymentPreferenceModalProps
> = ({
  open,
  rate,
  initialFrequency,
  isInitialFrequencyLoading = false,
  frequencyBannerMessages,
  onClose,
  onSaveAgentPreference,
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  const frequencyOptions = useMemo(() => {
    if (!rate) {
      return [];
    }

    return buildPaymentPlanFrequencyOptions(rate);
  }, [rate]);

  const initialSelectedFrequency = useMemo(() => {
    if (!frequencyOptions.length) {
      return null;
    }

    if (
      initialFrequency &&
      frequencyOptions.some((option) => option.value === initialFrequency)
    ) {
      return initialFrequency;
    }

    if (!rate) {
      return frequencyOptions[0]?.value ?? null;
    }

    const selectedFrequency = mapPaymentPlanToOption(
      getSelectedPaymentPlan(rate)
    ).name;

    if (frequencyOptions.some((option) => option.value === selectedFrequency)) {
      return selectedFrequency;
    }

    return frequencyOptions[0]?.value ?? null;
  }, [frequencyOptions, initialFrequency, rate]);

  const [selectedFrequency, setSelectedFrequency] = useState(
    initialSelectedFrequency
  );

  const bannerMessages = useMemo(() => {
    return {
      ...DEFAULT_FREQUENCY_BANNER_MESSAGES,
      ...frequencyBannerMessages,
    };
  }, [frequencyBannerMessages]);

  const selectedFrequencyBanner = selectedFrequency
    ? bannerMessages[selectedFrequency]
    : null;
  const isSaveButtonDisabled =
    !selectedFrequency || isInitialFrequencyLoading || isSaving;

  useEffect(() => {
    const atlasModal = modalContentRef.current?.parentElement;

    if (!open || !atlasModal) {
      return;
    }

    atlasModal.classList.add(styles.modalContainer);

    return () => {
      atlasModal.classList.remove(styles.modalContainer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedFrequency(initialSelectedFrequency);
  }, [initialSelectedFrequency, open]);

  const updateAgentPaymentPreference = async () => {
    if (!selectedFrequency || !onSaveAgentPreference) {
      return;
    }

    setIsSaving(true);

    try {
      await onSaveAgentPreference(selectedFrequency);
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      controlled
      open={open}
      showCloseIcon={false}
      handleClose={onClose}
      variant="normal"
    >
      {() => (
        <div
          ref={modalContentRef}
          className={styles.content}
          data-testid="agent-payment-preference-modal"
        >
          <h2 className={styles.title}>Default payment plan</h2>
          <p className={styles.subtitle}>
            New quotes will start with this payment plan. You can always adjust
            it per customer.
          </p>

          <div className={styles.formSection}>
            <AgentPaymentPreferenceFrequencySelector
              value={selectedFrequency}
              options={frequencyOptions}
              disabled={
                !frequencyOptions.length ||
                isSaving ||
                isInitialFrequencyLoading
              }
              onChange={setSelectedFrequency}
            />

            {selectedFrequencyBanner && (
              <div
                className={styles.infoBanner}
                data-testid="agent-payment-preference-banner"
              >
                <img
                  src={lightbulbIcon}
                  alt=""
                  aria-hidden="true"
                  className={styles.weeklyBannerIcon}
                />
                <p>{selectedFrequencyBanner}</p>
              </div>
            )}
          </div>

          <div className={styles.actions}>
            <Button
              link
              type="button"
              className={`${styles.actionButton} ${styles.cancelButton}`}
              data-testid="agent-payment-preference-cancel"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              type="button"
              className={`${styles.actionButton} ${styles.saveButton}`}
              data-testid="agent-payment-preference-save"
              isSubmitting={isSaving}
              disabled={isSaveButtonDisabled}
              onClick={updateAgentPaymentPreference}
            >
              Save Default
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
